import React, { createContext, CSSProperties, ReactNode, useContext, useRef, useState } from 'react';
import { Button } from 'semantic-ui-react';
import './styles.css';

type Position = {
    x: number;
    y: number;
};

type ZoomContextValueType = {
    zoom: number;
    position: Position;
    isDragging: boolean;
    minZoom: number;
    maxZoom: number;
    zoomStep: number;
    containerRef: React.RefObject<HTMLDivElement>;
    contentRef: React.RefObject<HTMLDivElement>;
    handleZoomIn: () => void;
    handleZoomOut: () => void;
    handleReset: () => void;
    handleMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
    handleMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void;
    handleMouseUp: () => void;
    containerStyle: CSSProperties;
    contentStyle: CSSProperties;
};

const ZoomContext = createContext<ZoomContextValueType | null>(null);

type ZoomContextProviderPropsType = {
    intialState: ZoomContextValueType;
    children: ReactNode;
};
function ZoomContextProvider({ children, intialState }: ZoomContextProviderPropsType) {
    return <ZoomContext.Provider value={intialState}>{children}</ZoomContext.Provider>;
}
const useZoom = () => {
    const context = useContext(ZoomContext);
    if (!context) {
        throw new Error('Zoom compound components must be used within ZoomableContainer');
    }
    return context;
};

const DEFUALT_VALUES = {
    MIN_ZOOM: 0.5,
    MAX_ZOOM: 3,
    ZOOM_STEP: 0.2,
    SHOW_CONTROLS: true,
    BASE_ZOOM: 1,
};

type ZoomPropsType = {
    children: ReactNode;
    minZoom?: number;
    maxZoom?: number;
    zoomStep?: number;
    initialZoom?: number;
};

function Zoom({
    children,
    minZoom = DEFUALT_VALUES.MIN_ZOOM,
    maxZoom = DEFUALT_VALUES.MAX_ZOOM,
    zoomStep = DEFUALT_VALUES.ZOOM_STEP,
    initialZoom = DEFUALT_VALUES.BASE_ZOOM,
}: ZoomPropsType) {
    const [zoom, setZoom] = useState<number>(initialZoom);
    const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [dragStart, setDragStart] = useState<Position>({ x: 0, y: 0 });
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    // Constrain position to boundaries
    const constrainPosition = (pos: Position, currentZoom: number): Position => {
        if (!containerRef.current || !contentRef.current) {
            return pos;
        }

        // Get the viewport dimensions (the "window" we look through)
        const containerRect = containerRef.current.getBoundingClientRect();

        // Get the original content dimensions BEFORE any zoom transform is applied
        // Using offsetWidth/Height because getBoundingClientRect would include the transform
        const contentWidth = containerRef.current.offsetWidth;
        const contentHeight = containerRef.current.offsetHeight;

        // Calculate how big the content is AFTER zoom is applied
        // Example: 800px wide content at 2.0x zoom = 1600px
        const scaledWidth = contentWidth * currentZoom;
        const scaledHeight = contentHeight * currentZoom;

        // Calculate maximum allowed pan distance in each direction
        //
        // The formula: (scaledSize - containerSize) / 2
        //
        // Example 1 - Zoomed IN (2.0x):
        //   scaledWidth = 1600, containerWidth = 800
        //   maxX = (1600 - 800) / 2 = 400
        //   → Can pan 400px left OR right (800px total range)
        //
        // Example 2 - Normal size (1.0x):
        //   scaledWidth = 800, containerWidth = 800
        //   maxX = (800 - 800) / 2 = 0
        //   → No panning allowed, content stays centered
        //
        // Example 3 - Zoomed OUT (0.5x):
        //   scaledWidth = 400, containerWidth = 800
        //   maxX = (400 - 800) / 2 = -200 → Math.max(0, -200) = 0
        //   → No panning allowed, content stays centered
        //
        // Math.max(0, ...) ensures we never get negative boundaries
        const maxX = Math.max(0, (scaledWidth - containerRect.width) / 2);
        const maxY = Math.max(0, (scaledHeight - containerRect.height) / 2);

        // Clamp the position to stay within [-maxX, maxX] and [-maxY, maxY]
        //
        // Math.max(pos.x, -maxX) ensures: pos.x >= -maxX (not too far left)
        // Math.min(..., maxX) ensures: pos.x <= maxX (not too far right)
        //
        // Example with maxX = 400:
        //   pos.x = 500 → clamped to 400 (too far right)
        //   pos.x = -500 → clamped to -400 (too far left)
        //   pos.x = 200 → stays 200 (within bounds)
        return {
            x: Math.min(Math.max(pos.x, -maxX), maxX),
            y: Math.min(Math.max(pos.y, -maxY), maxY),
        };
    };

    const handleZoomIn = (): void => {
        const newZoom = Math.min(zoom + zoomStep, maxZoom);
        setZoom(newZoom);
        setPosition((prev) => constrainPosition(prev, newZoom));
    };

    const handleZoomOut = (): void => {
        const newZoom = Math.max(zoom - zoomStep, minZoom);
        setZoom(newZoom);
        setPosition((prev) => constrainPosition(prev, newZoom));
    };

    const handleReset = (): void => {
        setZoom(initialZoom);
        setPosition({ x: 0, y: 0 });
    };

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
        e.preventDefault(); // Prevent text selection and default drag behavior
        if (zoom > DEFUALT_VALUES.BASE_ZOOM) {
            setIsDragging(true);
            setDragStart({
                x: e.clientX - position.x,
                y: e.clientY - position.y,
            });
        }
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>): void => {
        if (isDragging) {
            const newPosition = {
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y,
            };
            setPosition(constrainPosition(newPosition, zoom));
        }
    };

    const handleMouseUp = (): void => {
        setIsDragging(false);
    };

    const containerStyle: CSSProperties = {
        cursor: isDragging ? 'grabbing' : zoom > 1 ? 'grab' : 'default',
    };

    const contentStyle: CSSProperties = {
        transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
        transformOrigin: 'center center',
    };

    return (
        <ZoomContextProvider
            intialState={{
                contentRef,
                containerRef,
                containerStyle,
                contentStyle,
                handleMouseDown,
                handleMouseMove,
                handleMouseUp,
                handleReset,
                handleZoomIn,
                handleZoomOut,
                isDragging,
                maxZoom,
                minZoom,
                position,
                zoom,
                zoomStep,
            }}
        >
            {children}
        </ZoomContextProvider>
    );
}

type ZoomContainerPropsType = {
    className?: string;
    children: ReactNode;
};

function ZoomContainer({ children, className = '' }: ZoomContainerPropsType) {
    return <div className={`zoomable-container ${className}`}>{children}</div>;
}

type ZoomControlsPropsType = {
    className?: string;
};
function ZoomControls({ className = '' }: ZoomControlsPropsType) {
    const { handleZoomIn, handleZoomOut, handleReset } = useZoom();

    return (
        <div className={`zoom-controls-container ${className}`}>
            <Button icon="zoom-out" onClick={handleZoomOut} />
            <Button icon="repeat" onClick={handleReset} />
            <Button icon="zoom-in" onClick={handleZoomIn} />
        </div>
    );
}

type ZoomContentPropsType = {
    children: ReactNode;
};

function ZoomContent({ children }: ZoomContentPropsType) {
    const { containerRef, handleMouseDown, handleMouseMove, handleMouseUp, containerStyle, contentRef, contentStyle } =
        useZoom();

    return (
        <div
            className="zoomable-content-container"
            ref={containerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={containerStyle}
        >
            <div ref={contentRef} style={contentStyle} onDragStart={(e) => e.preventDefault()}>
                {children}
            </div>
        </div>
    );
}

Zoom.Container = ZoomContainer;
Zoom.Controls = ZoomControls;
Zoom.Content = ZoomContent;

type ZoomableContainerPropsType = {
    children: ReactNode;
    minZoom?: number;
    maxZoom?: number;
    zoomStep?: number;
    showControls?: boolean;
    initialZoom?: number;
};

export function ZoomableContainer({
    children,
    minZoom = DEFUALT_VALUES.MIN_ZOOM,
    maxZoom = DEFUALT_VALUES.MAX_ZOOM,
    zoomStep = DEFUALT_VALUES.ZOOM_STEP,
    showControls = DEFUALT_VALUES.SHOW_CONTROLS,
    initialZoom = DEFUALT_VALUES.BASE_ZOOM,
}: ZoomableContainerPropsType) {
    return (
        <Zoom minZoom={minZoom} maxZoom={maxZoom} zoomStep={zoomStep} initialZoom={initialZoom}>
            <Zoom.Container>
                {showControls && <Zoom.Controls />}
                <Zoom.Content>{children}</Zoom.Content>
            </Zoom.Container>
        </Zoom>
    );
}

export default Zoom;
